"use client";
import React, { useState, useEffect, useMemo } from "react";
import Image from "next/image";
import api from "@/lib/axiosInstance";
import { Search, MapPin, Star, Recycle, ArrowRight, Leaf, X } from "lucide-react";
import Link from "next/link";
import { motion } from "framer-motion";

const wasteTypes = ["All", "Solar Panels", "Lithium-Ion Batteries", "Lead-Acid Batteries", "Inverters", "Charge Controllers"];

function getAvgRating(reviews: any[]): string {
  if (!reviews || reviews.length === 0) return "New";
  const avg = reviews.reduce((s: number, r: any) => s + r.rating, 0) / reviews.length;
  return avg.toFixed(1);
}

export default function RecyclersComponent() {
  const [recyclers, setRecyclers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeWaste, setActiveWaste] = useState("All");

  useEffect(() => {
    api.get("/recyclers")
      .then(res => setRecyclers(res.data.recyclers || []))
      .catch(err => console.error("Failed to fetch recyclers", err))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return recyclers.filter(r => {
      const name = (r.name || "").toLowerCase();
      const city = (r.city || "").toLowerCase();
      const specialty = (r.recyclerProfile?.areaOfInterest || "").toLowerCase();
      const q = search.toLowerCase();
      const matchSearch = !q || name.includes(q) || city.includes(q) || specialty.includes(q);
      const matchWaste = activeWaste === "All" || specialty.includes(activeWaste.toLowerCase());
      return matchSearch && matchWaste;
    });
  }, [recyclers, search, activeWaste]);

  return (
    <div className="min-h-screen bg-[#f4f6f6]">
      {/* Hero */}
      <section className="bg-[#002301] pt-28 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="recycler-dots" width="30" height="30" patternUnits="userSpaceOnUse">
                <circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#recycler-dots)" />
          </svg>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#17BC2E]/10 rounded-full blur-3xl pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-[#17BC2E]/20 border border-[#17BC2E]/30 text-[#17BC2E] text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
            <Leaf className="w-4 h-4" /> Certified Recyclers
          </div>
          <h1 className="text-white text-4xl sm:text-5xl font-bold leading-tight mb-4">
            Find Solar <span className="text-[#FFB600]">Recyclers</span> Near You
          </h1>
          <p className="text-white/60 text-base max-w-xl mx-auto leading-relaxed mb-8">
            Connect with verified recyclers who responsibly handle solar panels, batteries, and inverters across Nigeria.
          </p>

          {/* Search bar */}
          <div className="max-w-xl mx-auto relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, location, or specialty..."
              className="w-full pl-12 pr-12 py-3.5 rounded-2xl bg-white text-gray-800 placeholder:text-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFB600] shadow-lg"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.div>
      </section>

      {/* Waste type filter chips */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex gap-2 overflow-x-auto scrollbar-hide">
          {wasteTypes.map(type => (
            <button
              key={type}
              onClick={() => setActiveWaste(type)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                activeWaste === type
                  ? "bg-[#002301] text-white"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <section className="py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Count */}
          <p className="text-sm text-gray-500 mb-6">
            {loading ? "Loading..." : `${filtered.length} recycler${filtered.length !== 1 ? "s" : ""} found`}
          </p>

          {/* Loading skeleton */}
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 animate-pulse">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-xl" />
                    <div className="flex-1">
                      <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
                      <div className="h-2.5 bg-gray-100 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded w-full mb-2" />
                  <div className="h-2.5 bg-gray-100 rounded w-3/4" />
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && filtered.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Recycle className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-700 font-semibold text-lg mb-1">No recyclers found</p>
              <p className="text-gray-400 text-sm">Try adjusting your search or filter.</p>
              {(search || activeWaste !== "All") && (
                <button
                  onClick={() => { setSearch(""); setActiveWaste("All"); }}
                  className="mt-4 text-[#002301] text-sm font-semibold underline underline-offset-2 hover:text-[#17BC2E] transition-colors"
                >
                  Clear filters
                </button>
              )}
            </motion.div>
          )}

          {/* Cards grid */}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((r, i) => {
                const avgRating = getAvgRating(r.receivedReviews);
                const reviewCount = r.receivedReviews?.length ?? 0;
                const specialty = r.recyclerProfile?.areaOfInterest || "General Recycling";

                return (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.4 }}
                  >
                    <Link href={`/recyclers/${r.id}`}>
                      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 overflow-hidden flex flex-col">
                        {/* Top accent bar */}
                        <div className="h-1 bg-gradient-to-r from-[#002301] to-[#17BC2E]" />

                        <div className="p-5 flex flex-col flex-1">
                          {/* Header row */}
                          <div className="flex items-start gap-3 mb-4">
                            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-[#f0f7ee] border border-[#002301]/10">
                              <Image
                                src={r.profileImage || "/assets/img/recycler-img.png"}
                                alt={r.name}
                                width={48}
                                height={48}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 text-sm leading-snug truncate">{r.name}</h3>
                              {r.city && (
                                <div className="flex items-center gap-1 text-gray-400 text-xs mt-0.5">
                                  <MapPin className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{r.city}</span>
                                </div>
                              )}
                            </div>
                            {/* Rating badge */}
                            <div className="flex items-center gap-1 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1 flex-shrink-0">
                              <Star className="w-3 h-3 fill-[#FFB600] text-[#FFB600]" />
                              <span className="text-xs font-semibold text-gray-700">{avgRating}</span>
                            </div>
                          </div>

                          {/* Specialty */}
                          <div className="flex items-center gap-2 mb-4">
                            <Recycle className="w-3.5 h-3.5 text-[#17BC2E] flex-shrink-0" />
                            <p className="text-sm text-gray-600 leading-snug line-clamp-2">{specialty}</p>
                          </div>

                          {/* Footer */}
                          <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-50">
                            <span className="text-xs text-gray-400">
                              {reviewCount > 0 ? `${reviewCount} review${reviewCount !== 1 ? "s" : ""}` : "No reviews yet"}
                            </span>
                            <span className="inline-flex items-center gap-1 text-[#002301] text-xs font-semibold hover:text-[#17BC2E] transition-colors">
                              View Profile <ArrowRight className="w-3 h-3" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
